// ⚠️ NO RELATIVE IMPORTS — a bundler compiles this from source; web's protocol-imports.leaf.test.ts enforces it.
/** App-level Slack message shortcut for opening the controls of the session that
 * owns the selected message's conversation. Direct apps receive it over Socket
 * Mode; shared apps receive the same callback through the relay HTTP edge.
 *
 * Declared to Slack by {@link buildSlackAppManifest} below, which is why it lives
 * here rather than beside the runtime Block Kit action ids in `frames/relay-cp.ts`:
 * those name controls on messages we post, this one names a manifest feature. */
export const SLACK_MANAGE_SESSION_SHORTCUT_CALLBACK_ID = 'ac_manage_session';
/** Public platform profile copy. Never derive this from an Agent description. */
export const PLATFORM_APP_DESCRIPTION = 'AI agent powered by AgentConnect.';
/**
 * Every bot scope the app requests, and the exact set an installation must hold.
 *
 * There was briefly a required/capability split here, on the theory that a scope backing one
 * optional tool should not mark an older installation broken. It did not survive contact: the
 * "required" half was not required either — the app receives and answers messages without
 * several of them — so the line was invented rather than observed, and it bought a second
 * constant, an extra API field, extra console branches, and a message that said six permissions
 * were missing without naming them. One list is both simpler and more honest: these are the
 * scopes AgentConnect needs, an install short of any of them is incomplete, and the console
 * says exactly which to add.
 */
export const SLACK_BOT_SCOPES = [
    'files:read',
    'app_mentions:read',
    'channels:history',
    'channels:read',
    'commands',
    'chat:write',
    'chat:write.customize',
    'files:write',
    'groups:history',
    'groups:read',
    'im:history',
    // `conversations.info` needs one of channels/groups/im/mpim `:read` PER CONVERSATION TYPE,
    // and this one was the gap: the other three were declared and the DM arm was not. It is not
    // cosmetic — `sendMessage` into a DM asks `getChannelInfo` whether the target is one, and a
    // refused lookup falls back to "not a DM", which keys the outbound session as a channel
    // thread while the inbound messages key it as a DM, so the conversation forks.
    'im:read',
    'im:write',
    'mpim:history',
    'mpim:read',
    'reactions:read',
    'reactions:write',
    'assistant:write',
    'users:read',
    'canvases:read',
    'canvases:write',
    // `conversations.leave` has always needed one of these two; the manifest never asked.
    'channels:manage',
    'groups:write',
    'mpim:write',
    'bookmarks:read',
    'bookmarks:write',
    'lists:read',
    'lists:write',
    // RESERVED — declared deliberately ahead of a caller, which the rule above otherwise forbids.
    // Every scope added later costs a reinstall of every installation, so the ones we know are
    // coming ride along with these and cost nothing extra: `channels:join` lets a future agent
    // add itself to a channel instead of waiting to be invited, and the two directory reads are
    // for console-side operator screens. Nothing calls them yet; that is the point of naming them
    // here rather than discovering them one reinstall at a time.
    'channels:join',
    'team:read',
    'users:read.email',
    // `search:read.*` arrives HERE, with `searchPublicMessages` — the rule this list states, that a
    // scope may only land alongside the capability that calls it, since anything in this list
    // makes every existing install incomplete. PUBLIC only, and that is
    // measured rather than cautious: Slack grants `search:read.private` / `.im` / `.mpim` to a bot
    // — its scope reference says user tokens only, which is wrong — but they return nothing. With
    // the bot a MEMBER of a private channel, no combination of `channel_types` surfaced a message
    // posted there, and a DM's text matched nothing at all. Anthropic's own Slack app makes the
    // same split: bot search is public, private and DM search is a per-user grant.
    'search:read.public',
    'search:read.files',
    'search:read.users'
];
// Both transports advertise the same events: Socket Mode receives them directly, and the relay's
// HTTP ingress forwards the ones this app acts on to the daemon that owns the conversation.
export const SLACK_BOT_EVENTS = [
    // The native stop button on an agent session; without the subscription Slack never shows it.
    'agent_session_stopped',
    // Subscribed but not handled yet, so title sync can land without a second manifest refresh.
    'agent_session_title_changed',
    'app_mention',
    'app_uninstalled',
    'assistant_thread_started',
    // Same: the shipping context signal for an assistant thread, pre-provisioned and inert today.
    'assistant_thread_context_changed',
    'message.channels',
    'message.groups',
    'message.im',
    'message.mpim',
    'member_joined_channel',
    'channel_left',
    'group_left',
    'tokens_revoked'
];
export const DEFAULT_SLACK_APP_NAME = 'agentconnect';
export function slackEventsRequestUrl(relayUrl) {
    return `${normalizeSlackRelayUrl(relayUrl)}/slack/events`;
}
export function slackInteractionsRequestUrl(relayUrl) {
    return `${normalizeSlackRelayUrl(relayUrl)}/slack/interactions`;
}
function normalizeSlackRelayUrl(relayUrl) {
    return relayUrl.replace(/^ws(s?):\/\//i, 'http$1://').replace(/\/+$/, '');
}
/** One canonical Slack manifest shared by browser, Control Plane, and Setup Server. */
export function buildSlackAppManifest(name, options = {}) {
    const appName = name.trim() || DEFAULT_SLACK_APP_NAME;
    const displayName = options.displayName?.trim() || appName;
    const relayUrl = options.relayUrl ? normalizeSlackRelayUrl(options.relayUrl) : undefined;
    const http = Boolean(relayUrl);
    const redirectUrls = [...new Set(options.redirectUrls ?? [])];
    return {
        display_information: {
            name: appName,
            ...(options.backgroundColor ? { background_color: options.backgroundColor } : {})
        },
        features: {
            bot_user: { display_name: displayName, always_online: true },
            app_home: { home_tab_enabled: false, messages_tab_enabled: true, messages_tab_read_only_enabled: false },
            agent_view: {
                agent_description: PLATFORM_APP_DESCRIPTION,
                suggested_prompts: []
            },
            shortcuts: [
                {
                    name: 'Manage session',
                    type: 'message',
                    callback_id: SLACK_MANAGE_SESSION_SHORTCUT_CALLBACK_ID,
                    description: 'View or update the AgentConnect session for this conversation'
                }
            ]
        },
        oauth_config: {
            scopes: { bot: [...SLACK_BOT_SCOPES] },
            ...(redirectUrls.length > 0 ? { redirect_urls: redirectUrls } : {}),
            ...(options.pkceEnabled !== undefined ? { pkce_enabled: options.pkceEnabled } : {})
        },
        settings: {
            event_subscriptions: {
                bot_events: [...SLACK_BOT_EVENTS],
                ...(relayUrl ? { request_url: slackEventsRequestUrl(relayUrl) } : {})
            },
            interactivity: {
                is_enabled: true,
                ...(relayUrl
                    ? {
                        request_url: slackInteractionsRequestUrl(relayUrl),
                        message_menu_options_url: slackInteractionsRequestUrl(relayUrl)
                    }
                    : {})
            },
            org_deploy_enabled: false,
            socket_mode_enabled: !http,
            token_rotation_enabled: false,
            is_mcp_enabled: false
        }
    };
}
//# sourceMappingURL=slack-app-manifest.js.map